import type { Lot } from '../../types/lot'

type LineageLotOption = Pick<Lot, 'lotKey' | 'productionUnitId'>
type MergeLineageLot = Pick<Lot, 'productionUnitId'>
type LineageLotLocation = Pick<Lot, 'farmId' | 'productionUnitId'>
type GroupedLineageLot = Pick<Lot, 'id' | 'lotKey' | 'productionUnitId'>
type MergeSourceChipLot = Pick<Lot, 'id' | 'lotKey' | 'productionUnitId'>

export type LineageLotOptionGroup = {
  productionUnitId: string
  label: string
  options: Array<{
    id: string
    label: string
  }>
}

export type LineageLotQuickOption = {
  id: string
  label: string
  productionUnitId: string
  productionUnitLabel: string
}

export type MergeSourceSelectionSummary = {
  selectedCount: number
  productionUnitIds: string[]
  sharedProductionUnitId: string | null
  canMerge: boolean
}

export type MergeSourceReviewState = {
  totalCount: number
  visibleCount: number
  hiddenCount: number
  canToggle: boolean
  isCollapsed: boolean
}

export type MergeSourceChipGroup = {
  productionUnitId: string
  productionUnitLabel: string
  lots: MergeSourceChipLot[]
}

export type MergeSourceReorderDirection = 'earlier' | 'later'

const collectProductionUnitIds = (lots: MergeLineageLot[]): string[] => {
  const seen = new Set<string>()
  const productionUnitIds: string[] = []

  lots.forEach((lot) => {
    const productionUnitId = String(lot.productionUnitId ?? '').trim()
    if (!productionUnitId || seen.has(productionUnitId)) return
    seen.add(productionUnitId)
    productionUnitIds.push(productionUnitId)
  })

  return productionUnitIds
}

const resolveProductionUnitLabel = (
  productionUnitId: string | undefined,
  productionUnitLookup: ReadonlyMap<string, string>
): string => {
  const normalizedProductionUnitId = String(productionUnitId ?? '').trim()
  if (!normalizedProductionUnitId) return ''
  return productionUnitLookup.get(normalizedProductionUnitId)?.trim() || normalizedProductionUnitId
}

const normalizeSearchValue = (value: string): string => value.trim().toLowerCase()

export const formatLineageLotOptionLabel = (
  lot: LineageLotOption,
  productionUnitLookup: ReadonlyMap<string, string>
): string => {
  const unitLabel = resolveProductionUnitLabel(lot.productionUnitId, productionUnitLookup)
  if (!unitLabel) {
    return lot.lotKey
  }

  return `${lot.lotKey} (${unitLabel})`
}

export const formatLineageLotLocationLabel = (
  lot: LineageLotLocation,
  lookups: {
    farmLookup: ReadonlyMap<string, string>
    productionUnitLookup: ReadonlyMap<string, string>
  }
): string => {
  const farmLabel = lookups.farmLookup.get(lot.farmId)?.trim() || lot.farmId
  const unitId = String(lot.productionUnitId ?? '').trim()
  const unitLabel = unitId ? (lookups.productionUnitLookup.get(unitId)?.trim() || unitId) : ''
  return [farmLabel, unitLabel]
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
    .join(' / ')
}

export const buildLineageLotOptionGroups = (
  lots: GroupedLineageLot[],
  args: {
    productionUnitLookup: ReadonlyMap<string, string>
    preferredProductionUnitId?: string
  }
): LineageLotOptionGroup[] => {
  const groupsByProductionUnitId = new Map<string, GroupedLineageLot[]>()

  lots.forEach((lot) => {
    const productionUnitId = String(lot.productionUnitId ?? '').trim()
    const nextLots = groupsByProductionUnitId.get(productionUnitId) ?? []
    nextLots.push(lot)
    groupsByProductionUnitId.set(productionUnitId, nextLots)
  })

  return Array.from(groupsByProductionUnitId.entries())
    .sort(([leftProductionUnitId], [rightProductionUnitId]) => {
      const preferredProductionUnitId = args.preferredProductionUnitId?.trim() || ''
      const leftIsPreferred = preferredProductionUnitId.length > 0 && leftProductionUnitId === preferredProductionUnitId
      const rightIsPreferred = preferredProductionUnitId.length > 0 && rightProductionUnitId === preferredProductionUnitId

      if (leftIsPreferred && !rightIsPreferred) return -1
      if (!leftIsPreferred && rightIsPreferred) return 1

      const leftLabel = resolveProductionUnitLabel(leftProductionUnitId, args.productionUnitLookup)
      const rightLabel = resolveProductionUnitLabel(rightProductionUnitId, args.productionUnitLookup)
      return leftLabel.localeCompare(rightLabel)
    })
    .map(([productionUnitId, groupedLots]) => ({
      productionUnitId,
      label: resolveProductionUnitLabel(productionUnitId, args.productionUnitLookup),
      options: [...groupedLots]
        .sort((left, right) => left.lotKey.localeCompare(right.lotKey))
        .map((lot) => ({
          id: lot.id,
          label: lot.lotKey
        }))
    }))
}

export const buildLineageLotQuickOptions = (
  groups: LineageLotOptionGroup[]
): LineageLotQuickOption[] => (
  groups.flatMap((group) => group.options.map((option) => ({
    id: option.id,
    label: option.label,
    productionUnitId: group.productionUnitId,
    productionUnitLabel: group.label
  })))
)

export const filterLineageLotQuickOptions = (
  options: LineageLotQuickOption[],
  query: string,
  limit = options.length
): LineageLotQuickOption[] => {
  const normalizedQuery = normalizeSearchValue(query)
  if (!normalizedQuery) {
    return options.slice(0, limit)
  }

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean)

  return options
    .map((option, index) => {
      const searchText = normalizeSearchValue([
        option.label,
        option.productionUnitLabel,
        option.productionUnitId
      ].join(' '))

      if (!queryTokens.every((token) => searchText.includes(token))) {
        return null
      }

      const label = normalizeSearchValue(option.label)
      const productionUnitLabel = normalizeSearchValue(option.productionUnitLabel)
      const score = (
        label.startsWith(normalizedQuery) || productionUnitLabel.startsWith(normalizedQuery)
      )
        ? 0
        : 1

      return {
        option,
        index,
        score
      }
    })
    .filter((entry): entry is { option: LineageLotQuickOption; index: number; score: number } => Boolean(entry))
    .sort((left, right) => {
      const scoreDiff = left.score - right.score
      if (scoreDiff !== 0) return scoreDiff
      return left.index - right.index
    })
    .slice(0, limit)
    .map((entry) => entry.option)
}

export const summarizeMergeSourceSelection = (
  lots: MergeLineageLot[]
): MergeSourceSelectionSummary => {
  const productionUnitIds = collectProductionUnitIds(lots)

  return {
    selectedCount: lots.length,
    productionUnitIds,
    sharedProductionUnitId: productionUnitIds.length === 1 ? productionUnitIds[0] : null,
    canMerge: lots.length >= 2 && productionUnitIds.length <= 1
  }
}

export const buildMergeSourceReviewState = (
  selectedCount: number,
  options?: {
    previewLimit?: number
    expanded?: boolean
  }
): MergeSourceReviewState => {
  const previewLimit = Math.max(1, options?.previewLimit ?? 3)
  const canToggle = selectedCount > previewLimit
  const isCollapsed = canToggle && !options?.expanded
  const visibleCount = isCollapsed ? previewLimit : selectedCount

  return {
    totalCount: selectedCount,
    visibleCount,
    hiddenCount: Math.max(0, selectedCount - visibleCount),
    canToggle,
    isCollapsed
  }
}

export const reorderMergeSourceLotIds = (
  lotIds: string[],
  lotId: string,
  direction: MergeSourceReorderDirection
): string[] => {
  const index = lotIds.indexOf(lotId)
  if (index === -1) {
    return lotIds
  }

  const targetIndex = direction === 'earlier' ? index - 1 : index + 1
  if (targetIndex < 0 || targetIndex >= lotIds.length) {
    return lotIds
  }

  const next = [...lotIds]
  const [value] = next.splice(index, 1)
  next.splice(targetIndex, 0, value)
  return next
}

export const buildMergeSourceChipGroups = (
  lots: MergeSourceChipLot[],
  productionUnitLookup: ReadonlyMap<string, string>
): MergeSourceChipGroup[] => {
  const groupsByProductionUnitId = new Map<string, MergeSourceChipLot[]>()
  const orderedProductionUnitIds: string[] = []

  lots.forEach((lot) => {
    const productionUnitId = String(lot.productionUnitId ?? '').trim()
    if (!groupsByProductionUnitId.has(productionUnitId)) {
      groupsByProductionUnitId.set(productionUnitId, [])
      orderedProductionUnitIds.push(productionUnitId)
    }

    groupsByProductionUnitId.get(productionUnitId)?.push(lot)
  })

  return orderedProductionUnitIds.map((productionUnitId) => ({
    productionUnitId,
    productionUnitLabel: resolveProductionUnitLabel(productionUnitId, productionUnitLookup),
    lots: groupsByProductionUnitId.get(productionUnitId) ?? []
  }))
}

export const mergeSourcesShareProductionUnit = (lots: MergeLineageLot[]): boolean => {
  return summarizeMergeSourceSelection(lots).productionUnitIds.length <= 1
}